import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import throttle from 'lodash/throttle';
import {
  mergeVehicleTracking,
  setStatisticVehicleStatus
} from 'src/features/vehicleSlice';
import { useAccessToken } from './useAccessToken';
import { getNormalizedVehicleStatus, LOST_GSM } from 'src/app/utils/vehicleService';

// Mã trường trong gói PERIODIC (bảng MQTT_DATA của cms_api) → tên trường mà danh sách xe đang
// dùng (vehicle.service.ts, extractInfoForTracking*). Mười trong số này trước đây bị vứt: gói đã
// mang chúng tới trình duyệt nhưng hook chỉ bóc 254/255/259/261, nên popup phải đi hỏi lại bằng
// GET /vehicles/detail/:id mỗi 10 giây.
export const WS_FIELD_BY_CODE = {
  253: 'gsm_level',
  255: 'direction',
  256: 'battery',
  257: 'charge_status',
  259: 'engine_status',
  261: 'speed_gps',
  262: 'speed_digital_meter',
  263: 'gps_status',
  264: 'door_status',
  265: 'satellites',
  268: 'gsm_signal',
  // Bộ nhớ còn trống (MB). Giữ NGUYÊN giá trị, kể cả -1: -1 là mã lỗi thiết bị báo khi không đọc
  // được bộ nhớ, việc dịch nó thành chữ là của giao diện.
  273: 'memory_card',
  280: 'location'
};

// Topic PERIODIC chở nhiều loại gói, phân biệt bằng trường 111: 1 = GPS định kỳ, 3 = tốc độ,
// 9 = cảm biến dầu. Hai loại sau không có toạ độ, tốc độ hay trạng thái động cơ.
const PERIODIC_GPS = 1;

/**
 * Dựng bản ghi cập nhật từ một khung WebSocket. Trả về null khi khung không dùng được.
 *
 * Hai lớp phòng vệ độc lập, cố ý không gộp làm một:
 *  1. Bỏ qua gói không phải GPS (cần dịch vụ realtime bản mới gửi `messageType`).
 *  2. KHÔNG gán khoá cho trường thiếu — vì `{ ...current, ...update }` coi `undefined` là giá trị
 *     hợp lệ và sẽ xoá số đang đúng của xe. Lớp này bảo vệ được ngay cả khi lớp 1 chưa có.
 */
export const buildVehicleUpdate = frame => {
  if (!frame) return null;

  const { deviceId, serial, data, timeStamp, status, messageType } = frame;
  if (!deviceId || !serial || !data) return null;

  // Dịch vụ realtime bản cũ không gửi messageType → coi như gói GPS, đúng hành vi trước đây.
  if (
    messageType !== undefined &&
    messageType !== null &&
    messageType !== PERIODIC_GPS
  ) {
    return null;
  }

  const update = { device_id: deviceId, device_serial: serial };

  if (timeStamp !== undefined && timeStamp !== null) update.created_at = timeStamp;
  if (status !== undefined && status !== null) update.device_status = status;

  Object.keys(WS_FIELD_BY_CODE).forEach(code => {
    const value = data[code];
    // Chuỗi rỗng cũng bỏ: thiết bị gửi địa chỉ rỗng thì giữ địa chỉ cũ, không xoá ô.
    if (value === undefined || value === null || value === '') return;
    update[WS_FIELD_BY_CODE[code]] = value;
  });

  const arr = data['254'];
  if (Array.isArray(arr)) {
    const [lat, lng] = arr;
    if (lat && lng) {
      update.lat = lat;
      update.lng = lng;
    }
  }

  return update;
};

// Trường quyết định "xe này có gì đổi không". `created_at` đã đổi theo từng gói nên việc thêm
// telemetry vào đây KHÔNG làm tăng số lượt dispatch.
const CHANGED_KEYS = [
  'lat',
  'lng',
  'created_at',
  'device_status',
  ...Object.keys(WS_FIELD_BY_CODE).map(code => WS_FIELD_BY_CODE[code])
];

const useVehicleTracking = () => {
  const dispatch = useDispatch();
  const store = useStore();

  // Authentication & IDs
  const accessToken = useAccessToken();

  const listVehicleTracking = useSelector(s => s.vehicleSlice.listVehicleTracking);
  const { userId, idSocket, driverId } = useSelector(s => s.authSlice);

  // Refs: data queue and vehicle map
  const queueRef = useRef([]);
  const mapRef = useRef({});
  const intervalTimerRef = useRef(null);
  // Danh sách do CHÍNH hook này vừa merge ra. mapRef đã được cập nhật tại chỗ trước khi
  // dispatch, nên effect "Initialize map" không dựng lại bảng cho đúng danh sách này.
  const selfListRef = useRef(null);

  // Chỉ gửi vào store những xe thật sự đổi; store giữ nguyên reference các xe còn lại.
  const dispatchChangedVehicles = useCallback(
    changedVehicles => {
      if (changedVehicles.length === 0) return;
      dispatch(mergeVehicleTracking(changedVehicles));
      selfListRef.current = store.getState().vehicleSlice.listVehicleTracking;
    },
    [dispatch, store]
  );

  // WebSocket URL
  const wsUrl = useMemo(() => {
    if (!idSocket || !accessToken) return null;
    if (userId) {
      return `${process.env.REACT_APP_REALTIME_WSS_SERVER}/realtime/${idSocket}/enduser?token=${accessToken}`;
    }
    if (driverId) {
      return `${process.env.REACT_APP_REALTIME_WSS_SERVER}/realtime/${idSocket}/driver?token=${accessToken}`;
    }
    return `${process.env.REACT_APP_REALTIME_WSS_SERVER}/realtime/${idSocket}/agency?token=${accessToken}`;
  }, [idSocket, accessToken]);

  // Process queued updates (throttled)
  const processQueue = useCallback(() => {
    const updates = queueRef.current.splice(0);
    const changedVehicles = [];
    updates.forEach(u => {
      const current = mapRef.current[u.device_id];
      if (!current) return;
      const merged = { ...current, ...u };

      // compare relevant fields
      const changed = CHANGED_KEYS.some(key => current[key] !== merged[key]);

      if (changed) {
        mapRef.current[u.device_id] = merged;
        changedVehicles.push(merged);
      }
    });

    dispatchChangedVehicles(changedVehicles);
  }, [dispatchChangedVehicles]);

  const throttledProcess = useMemo(
    () => throttle(processQueue, 500, { leading: true, trailing: true }),
    [processQueue]
  );

  // WebSocket setup
  const { sendJsonMessage, readyState } = useWebSocket(wsUrl, {
    share: false,
    shouldReconnect: () => true,
    // Hook không dùng lastMessage. Không có filter thì thư viện setState cho TỪNG gói,
    // tức một lượt commit React mỗi gói (đo được 45 commit/s ở 33 gói/s).
    filter: () => false,
    onOpen: () => {
      sendJsonMessage({
        event: 'subscribe',
        data: { channel: 'general-chatroom' }
      });
    },
    onMessage: useCallback(
      event => {
        if (!(event.data instanceof Blob)) return;
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const update = buildVehicleUpdate(JSON.parse(reader.result));
            if (!update) return;

            queueRef.current.push(update);

            throttledProcess();
          } catch (e) {
            console.error('WS parse error', e);
          }
        };
        reader.readAsText(event.data);
      },
      [throttledProcess]
    )
  });

  // Initialize map from Redux list
  useEffect(() => {
    if (!listVehicleTracking || listVehicleTracking.length === 0) {
      mapRef.current = {};
      return;
    }
    // Danh sách này do chính processQueue / handler 5s vừa merge ra — mapRef đã đúng.
    if (listVehicleTracking === selfListRef.current) return;

    const map = {};
    listVehicleTracking
      .filter(v => v.license_plate)
      .forEach(v => {
        map[v.device_id] = { ...v };
      });
    mapRef.current = map;
  }, [listVehicleTracking]);

  // Calculate stats & check lost_gms every 5s
  useEffect(() => {
    const handler = () => {
      // Don't process if no vehicles
      if (Object.keys(mapRef.current).length === 0) {
        // Reset stats to 0 when no vehicles
        dispatch(setStatisticVehicleStatus({
          moving: 0,
          stopped: 0,
          lost_gps: 0,
          lost_gsm: 0,
          over_speed: 0
        }));
        return;
      }

      const now = Date.now();
      const map = { ...mapRef.current }; // Clone to avoid direct mutation
      const lostVehicles = [];

      // Initialize stats from actual vehicle list
      const stats = {
        moving: 0,
        stopped: 0,
        lost_gps: 0,
        lost_gsm: 0,
        over_speed: 0
      };

      // First pass: Count stats and check for lost_gms
      Object.values(map).forEach(v => {
        // Count current status
        const normalized = getNormalizedVehicleStatus(v.device_status);
        if (normalized in stats) {
          stats[normalized]++;
        }

        // Check lost_gms logic
        const last = v.created_at * 1000;
        const diff = (now - last) / 1000;
        const shouldLost =
          (v.engine_status === 1 && diff > 300) ||  // 5 minutes
          (v.engine_status === 0 && diff > 3600);   // 60 minutes

        if (shouldLost && getNormalizedVehicleStatus(v.device_status) !== LOST_GSM) {
          const oldStatus = v.device_status;
          const device_id = v.device_id;

          // Update vehicle status
          map[device_id] = { ...v, device_status: LOST_GSM };
          lostVehicles.push(map[device_id]);

          // Adjust stats: -1 from old status, +1 to lost_gms
          if (oldStatus && stats[oldStatus] !== undefined) {
            stats[oldStatus] = Math.max(0, stats[oldStatus] - 1);
          }
          stats.lost_gsm++;
        }
      });

      // Dispatch updated stats
      dispatch(setStatisticVehicleStatus(stats));

      // Chỉ gửi những xe vừa chuyển sang lost_gsm
      if (lostVehicles.length > 0) {
        mapRef.current = map;
        dispatchChangedVehicles(lostVehicles);
      }
    };

    // Run immediately on mount
    handler();

    // Then run every 5 seconds
    intervalTimerRef.current = setInterval(handler, 5000);

    // Cleanup
    return () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
      }
    };
  }, [dispatch, dispatchChangedVehicles]);

  return { readyState, sendJsonMessage };
};

export default useVehicleTracking;
